import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Portal } from "@chakra-ui/portal";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../authContext';
import { 
  IoIosLogOut, 
  IoMdNotifications, 
  IoMdSettings,
  IoMdMenu,
  IoMdClose,
  IoMdArrowDown,
  IoMdArrowForward
} from "react-icons/io";
import { IncomingCallNotification } from "../../components/IncomingCallNotification";
import AskPanel from "../../components/AskPanel";
import { ActiveCallIndicator } from "../../components/ActiveCallIndicator";

function SidePanelIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M5 6v12" />
      <path d="M10 8.5h9" />
      <path d="M10 12h9" />
      <path d="M10 15.5h9" />
    </svg>
  );
}

export default function Admin(props: { [x: string]: any }) {
  const navigate = useNavigate();
  const { logout, displayPhoneNumber, email, isAuthenticated, verifiedName, setVerifiedName } = useAuth();
  const { ...rest } = props;
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});
  const [currentRoute, setCurrentRoute] = React.useState("Main Dashboard");
  
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  React.useEffect(() => {
    getActiveRoute(routes);
    // eslint-disable-next-line
  }, [location.pathname]);
  
  // functions for changing the states from components
  const getActiveRoute = (routes: RoutesType[]): string => {
    let activeRoute = "Dashboard";
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        // Check if current path matches this route
        const fullPath = `${routes[i].layout}${routes[i].path}`;
        if (location.pathname === fullPath || location.pathname === routes[i].path) {
          setCurrentRoute(routes[i].name);
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };
  
  const getActiveNavbar = (routes: RoutesType[]): boolean => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };
  
  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((prop, key) => {
      // Always render routes regardless of isShow - routes need to exist for navigation to work
      if (prop.layout === "/events") {
        // Render routes with layout="/events" - paths are relative to /events/*
        // In React Router v6, when inside a parent route with /*, child routes should be relative
        // Remove leading slash to make path relative to parent route
        let routePath = prop.path;
        if (routePath.startsWith('/')) {
          routePath = routePath.substring(1);
        }
        // Handle root path - empty string should use "index" route
        if (routePath === '' || routePath === '/') {
          return (
            <Route index element={prop.component} key={`events-index-${key}`} />
          );
        }
        return (
          <Route path={routePath} element={prop.component} key={`events-${key}`} />
        );
      } else if (prop.layout === 'linkedin') {
        return (
          <Route path={`${prop.path}`} element={prop.component} key={`linkedin-${key}`} />
        );
      }
      if (prop.collapse) {
        return getRoutes(prop.items);
      }
      // Render all other routes as well
      return (
        <Route path={`${prop.path}`} element={prop.component} key={`other-${key}`} />
      );
    }).filter(Boolean); // Remove any null/undefined routes
  };
  
  document.documentElement.dir = "ltr";

  const handleLogout = () => {
    logout();
    navigate('/signin');
  }

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-50
        transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto lg:flex-shrink-0
        ${sidebarCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className={`flex items-center border-b border-gray-100 py-5 ${sidebarCollapsed ? "justify-center px-2" : "justify-between px-6"}`}>
          <div className={`flex items-center ${sidebarCollapsed ? "" : "space-x-3"}`}>
            <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">W</span>
            </div>
            {!sidebarCollapsed && (
              <span className="text-lg font-bold text-gray-900">frontdesk24</span>
            )}
            {!sidebarCollapsed && (
              <button
                type="button"
                onClick={() => setSidebarCollapsed(true)}
                className="hidden h-10 w-10 items-center justify-center rounded-xl bg-[#d7e4f5] text-gray-800 hover:bg-[#c9d9ef] lg:flex"
                title="Hide sidebar"
                aria-label="Hide sidebar"
              >
                <SidePanelIcon />
              </button>
            )}
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-50"
          >
            <IoMdClose className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* User Info */}
        <div className={`${sidebarCollapsed ? 'px-2' : 'px-6'} py-4 border-b border-gray-100`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-gray-600 font-semibold text-sm">
                {verifiedName?.charAt(0) || 'U'}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {verifiedName || 'User'}
                </p>
                <p className="text-xs text-gray-500">
                  {displayPhoneNumber || 'No phone'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 space-y-1`}>
          {routes.filter(route => route.isShow).map((route) => {
            const isExpanded = expandedCategories[route.name];
            const isActive = location.pathname === route.path || 
              (route.collapse && route.items?.some(item => location.pathname === item.path));
            
            return (
              <div key={route.path}>
                {/* Main Category Button */}
                <button
                  onClick={() => {
                    if (route.collapse) {
                      toggleCategory(route.name);
                    } else {
                      navigate(route.path);
                      setSidebarOpen(false);
                    }
                  }}
                  className={`
                    w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-3 rounded-lg text-left
                    transition-all duration-200 ease-in-out
                    ${isActive 
                      ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                  title={sidebarCollapsed ? route.name : undefined}
                >
                  <span className="text-lg">{route.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="font-medium text-sm flex-1">{route.name}</span>
                      {route.collapse && (
                        <span className="text-gray-400">
                          {isExpanded ? <IoMdArrowDown size="16px" /> : <IoMdArrowForward size="16px" />}
                        </span>
                      )}
                    </>
                  )}
                </button>

                {/* Sub-items */}
                {route.collapse && route.items && isExpanded && !sidebarCollapsed && (
                  <div className="ml-6 space-y-1">
                    {route.items.filter(item => item.isShow).map((item) => (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                        className={`
                          w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left
                          transition-all duration-200 ease-in-out
                          ${location.pathname === item.path 
                            ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' 
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }
                        `}
                      >
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <span className="font-medium text-sm">{item.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-4 border-t border-gray-100`}>
          <div className="space-y-3">
            {/* Help & Support */}
            <button 
              type="button"
              onClick={() => window.dispatchEvent(new Event("open-ask"))}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all duration-200`}
              title={sidebarCollapsed ? "Help & Support" : undefined}
            >
              <span className="text-lg">💬</span>
              {!sidebarCollapsed && (
                <span className="font-medium text-sm">Help & Support</span>
              )}
            </button>
            
            {/* Version Info */}
            {!sidebarCollapsed && (
              <div className="px-3 py-2">
                <p className="text-xs text-gray-400 font-medium">Version 2.1.0</p>
                <p className="text-xs text-gray-400">© 2024 frontdesk24</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="flex flex-col gap-2">
            {/* Incoming Call Notification */}
            <IncomingCallNotification />
            <div className="flex items-center justify-between">
              {/* Left side */}
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  <IoMdMenu className="w-5 h-5 text-gray-600" />
                </button>
                {sidebarCollapsed ? (
                  <button
                    type="button"
                    onClick={() => setSidebarCollapsed(false)}
                    className="hidden h-10 w-10 items-center justify-center rounded-xl bg-[#d7e4f5] text-gray-800 hover:bg-[#c9d9ef] lg:flex"
                    title="Show sidebar"
                    aria-label="Show sidebar"
                  >
                    <SidePanelIcon />
                  </button>
                ) : null}
                <h1 className="text-xl font-semibold text-gray-900">
                  {currentRoute}
                </h1>
              </div>

              {/* Right side */}
              <div className="flex items-center space-x-4">
                {/* Active Call Indicator - Always visible when call is active */}
                <ActiveCallIndicator />
                
                {/* Notifications */}
                <button className="relative p-2 rounded-lg hover:bg-gray-50 transition-all duration-200">
                  <IoMdNotifications className="w-5 h-5 text-gray-500" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                    1
                  </span>
                </button>

                {/* Settings */}
                <button className="p-2 rounded-lg hover:bg-gray-50 transition-all duration-200">
                  <IoMdSettings className="w-5 h-5 text-gray-500" />
                </button>

                {/* Status */}
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600">Online</span>
                </div>

                {/* User Menu */}
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={handleLogout}
                    className="p-2 rounded-lg hover:bg-gray-50 transition-all duration-200"
                  >
                    <IoIosLogOut className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-6 py-2 min-h-0">
          <Routes>
            {getRoutes(routes)}
            {/* Catch-all route - only redirect if no route matched */}
            <Route path="*" element={<Navigate to="/events" replace />} />
          </Routes>
        </main>
      </div>
      <AskPanel />
    </div>
  );
}